/* ═════════════════════ the things that come round again ═════════════════════

   Recurring series: one template, many instances. A weekly reading, a problem
   set every fortnight, a discussion post every Thursday.

   The intake is the interesting part. Asking how many pages *before* the clock
   starts is what makes a rate measurable at all — minutes per page rather than
   minutes per reading — and a rate is the only rung of the ladder that can
   price work it has never seen. */
import { SERIES_KINDS, choiceAttrs, quantityAttr, seriesKind, wasStarted } from '../core/domain.js';
import { predictForAttrs, rateStats } from '../state/evidence.js';
import { DAY_MS, DOW, addDays, clamp, fmtDay, fromIsoDay, hhmmToMin, isoDay, startOfDay, uid } from '../core/time.js';
import { touchPlan } from '../state/plans.js';
import { S, courseColor, save } from '../state/store.js';
import { askText, closeModal, el, openModal, setDrawer, toast } from './dom.js';
import { renderLongterm } from './longterm.js';
import { refreshPlanningTabs } from './shell.js';
import { renderList, startTask } from './tasks.js';
import { renderWeekly } from './weekly.js';
import { $ } from './dom.js';
import { fmtDurMin } from '../core/time.js';
import { courseName } from '../state/store.js';

export const SERIES_HORIZON_DAYS = 21;

export const templateById = (id) => S.series.templates.find((t) => t.id === id) || null;

/** Every date a template fires on, between two moments, exceptions applied. */
export function occurrences(tpl, fromMs, toMs) {
  const out = [];
  const from = Math.max(startOfDay(fromMs).getTime(), tpl.from ? fromIsoDay(tpl.from) : -Infinity);
  const to = Math.min(toMs, tpl.until ? fromIsoDay(tpl.until) + DAY_MS - 1 : Infinity);
  if (!(from <= to)) return out;

  /* Walking the calendar rather than counting milliseconds. A term is long
     enough to cross a clock change, and after one the millisecond stride is an
     hour adrift: the weekday test then reads the day before, and a Tuesday
     series quietly becomes a Monday one for every week that follows. Nobody
     would ever see it inside a three-week generation window; on the horizon's
     sixteen weeks it is certain. */
  for (let ms = from; ms <= to; ms = addDays(new Date(ms), 1).getTime()) {
    const d = new Date(ms);
    if (!tpl.days.includes(d.getDay())) continue;

    const occ = isoDay(ms);
    const ex = tpl.exceptions[occ] || null;
    if (ex && ex.skip) { out.push({ occ, skipped: true, due: null }); continue; }

    const day = ex && ex.moveTo ? ex.moveTo : occ;
    const time = (ex && ex.time) || tpl.time;
    out.push({ occ, skipped: false, moved: !!(ex && ex.moveTo), day, time, due: fromIsoDay(day) + hhmmToMin(time) * 60000 });
  }
  return out;
}

/** The task an occurrence produced, if it has been generated. */
export const instanceOf = (tplId, occ) =>
  S.tasks.find((t) => t.series && t.series.templateId === tplId && t.series.occ === occ) || null;

/* Materialise everything due inside the horizon. Safe to call as often as you
   like: an occurrence already generated is left exactly as it is, including
   whatever work has been logged against it, and one that has since been
   skipped has its task withdrawn — but only while that task is still untouched.
   Time already logged is a fact, and a template edit does not get to erase it. */
export function generateSeries() {
  const now = Date.now();
  const horizon = now + SERIES_HORIZON_DAYS * DAY_MS;
  let made = 0;
  let withdrawn = 0;

  S.series.templates.forEach((tpl) => {
    if (tpl.paused) return;

    occurrences(tpl, now, horizon).forEach((o) => {
      const existing = instanceOf(tpl.id, o.occ);

      if (o.skipped) {
        if (existing && !existing.done && !wasStarted(existing)) {
          S.tasks = S.tasks.filter((t) => t.id !== existing.id);
          withdrawn++;
        }
        return;
      }

      if (existing) {
        // a re-dated occurrence keeps its task and its history; only when it
        // is due changes, and only while it has not been finished
        if (!existing.done && existing.series.due !== o.due) existing.series.due = o.due;
        return;
      }

      S.tasks.push({
        id: uid(), kind: 'task', title: tpl.title, courseId: tpl.courseId,
        estMin: tpl.estMin || 60,
        accSec: 0, runSince: null,
        done: false, multiple: null, actualMin: null,
        subtasks: [],
        deadlineId: tpl.deadlineId || null,
        start: null,
        created: now, completedAt: null,
        // what makes it an instance rather than a task that happens to be named
        // the same thing three weeks running
        series: { templateId: tpl.id, occ: o.occ, due: o.due, kind: tpl.kind },
        attrs: null,
      });
      made++;
    });
  });

  S.series.generatedThrough = isoDay(horizon);
  if (made || withdrawn) { touchPlan(); save(); }
  return { made, withdrawn };
}

/* ─────────────────── what a page actually costs ───────────────────

   historyStats() answers "how long does a problem set take her". This answers
   the sharper question: "how long does a page of a primary source take her, in
   this course". It is the same mining one level down — same finished tasks,
   same median, but divided by the quantity the work was measured in, and split
   by the choices that make two pages incomparable.

   Only finished instances carrying attributes can contribute, so this stays
   empty until a few have been timed. That is correct: a rate invented from no
   observations is worse than no rate at all. */

/** A series instance must say what it is made of before it can be timed. */
export const needsIntake = (t) => !!(t && t.series && seriesKind(t.series.kind).attrs.length && !t.attrs);

export let intakeId = null;

export function openIntake(id) {
  const t = S.tasks.find((x) => x.id === id);
  if (!t || !t.series) return;
  intakeId = id;

  const kind = seriesKind(t.series.kind);
  $('intakeTitle').textContent = t.title;
  $('intakeKind').textContent = `${kind.label} · ${courseName(t.courseId)}`;

  const host = $('intakeFields');
  host.textContent = '';

  kind.attrs.forEach((a) => {
    if (a.type === 'choice') {
      const field = el('div', 'field');
      field.appendChild(el('span', null, a.label));
      const seg = el('div', 'seg seg-sm');
      seg.dataset.attr = a.key;
      a.options.forEach((o) => {
        const b = el('button', 'seg-btn' + (o.value === a.default ? ' is-active' : ''), o.label);
        b.type = 'button';
        b.dataset.value = o.value;
        b.title = o.hint || '';
        b.addEventListener('click', () => {
          [...seg.children].forEach((x) => x.classList.toggle('is-active', x === b));
          updateIntakeEstimate();
        });
        seg.appendChild(b);
      });
      field.appendChild(seg);
      host.appendChild(field);
    } else if (a.type === 'quantity') {
      const field = el('label', 'field');
      field.appendChild(el('span', null, `${a.label} (${a.plural})`));
      const input = el('input');
      input.type = 'number';
      input.id = `intakeAttr_${a.key}`;
      input.min = String(a.min); input.max = String(a.max); input.step = '1';
      input.value = String(a.default);
      input.addEventListener('input', updateIntakeEstimate);
      field.appendChild(input);
      host.appendChild(field);
    }
  });

  updateIntakeEstimate();
  openModal('intakeModal');
  setTimeout(() => { const first = host.querySelector('input'); if (first) first.select(); }, 30);
}

/** Read the form back into the shape stored on the task. */
export function intakeValues() {
  const t = S.tasks.find((x) => x.id === intakeId);
  if (!t) return {};
  const out = {};
  seriesKind(t.series.kind).attrs.forEach((a) => {
    if (a.type === 'choice') {
      const on = $('intakeFields').querySelector(`[data-attr="${a.key}"] .is-active`);
      out[a.key] = on ? on.dataset.value : a.default;
    } else {
      out[a.key] = clamp(+($(`intakeAttr_${a.key}`) || {}).value || a.default, a.min, a.max);
    }
  });
  return out;
}

/* The estimate is offered, never imposed: the field stays editable, and the
   line under it says exactly which rung of the ladder the number came off. */
export function updateIntakeEstimate() {
  const t = S.tasks.find((x) => x.id === intakeId);
  if (!t) return;
  const p = predictForAttrs(t.courseId, t.series.kind, intakeValues(), t.estMin);

  $('intakeEstH').value = Math.floor(p.min / 60);
  $('intakeEstM').value = p.min % 60;

  const box = $('intakeBasis');
  box.textContent = `${fmtDurMin(p.min)} — ${p.basis}`;
  box.dataset.rung = p.rung;
}

export function confirmIntake() {
  const t = S.tasks.find((x) => x.id === intakeId);
  if (!t) return;

  const est = (+$('intakeEstH').value || 0) * 60 + (+$('intakeEstM').value || 0);
  if (est <= 0) { toast('Give it an estimate — the multiple is measured against it.', true); return; }

  t.attrs = intakeValues();
  /* Set before a single second is logged, so this is the first commitment and
     the multiple has an honest baseline to be measured against. */
  t.estMin = est;
  /* Saying how many pages it is moves this task up a rung: it can be priced
     per page now rather than off the median of unlike readings. */
  touchPlan();
  save();

  closeModal('intakeModal');
  const id = intakeId;
  intakeId = null;
  renderList();
  startTask(id);
}

/* ─────────────────── the series drawer ───────────────────

   Not a sixth tab. A template is configured once at the start of term and then
   read constantly — but read through its instances, which already appear in
   Current tasks, the day dock and the calendar. What the series itself needs is
   a workshop you open, adjust and close, which is exactly what the right-hand
   drawer slot is for. It becomes the fourth occupant of that slot. */

export let seriesEditId = null;

export function renderSeriesPanel() {
  if (!$('seriesList')) return;
  renderSeriesList();
  renderSeriesRates();
}

export function renderSeriesList() {
  const host = $('seriesList');
  host.textContent = '';

  const badge = $('seriesCount');
  badge.textContent = String(S.series.templates.length);
  badge.classList.toggle('is-zero', !S.series.templates.length);

  if (!S.series.templates.length) {
    host.appendChild(el('div', 'empty-list',
      'Nothing repeats yet. A reading due every Tuesday, a pset every other Friday — describe it once here and studyHub will keep putting it on your list.'));
    return;
  }

  S.series.templates.forEach((tpl) => {
    const k = seriesKind(tpl.kind);
    const row = el('div', 'sr-row' + (tpl.paused ? ' is-paused' : ''));
    row.style.setProperty('--course', courseColor(tpl.courseId));

    const top = el('div', 'sr-top');
    top.appendChild(el('span', 'sr-glyph', k.glyph));
    top.appendChild(el('span', 'sr-title', tpl.title));

    const ed = el('button', 'icon-btn', '✎');
    ed.title = 'Edit this series';
    ed.addEventListener('click', () => openSeriesEditor(tpl.id));
    top.appendChild(ed);

    const pause = el('button', 'icon-btn', tpl.paused ? '▷' : '❙❙');
    pause.title = tpl.paused ? 'Resume — start generating it again' : 'Pause — stop generating new ones, keep what exists';
    pause.addEventListener('click', () => {
      tpl.paused = !tpl.paused;
      if (tpl.paused) withdrawFuture(tpl.id);
      touchPlan();
      save(); generateSeries(); renderSeriesPanel(); renderList();
      refreshPlanningTabs();
      toast(tpl.paused ? 'Paused. Nothing new will be generated.' : 'Resumed.');
    });
    top.appendChild(pause);
    row.appendChild(top);

    const days = tpl.days.slice().sort().map((d) => DOW[d]).join(' ');
    row.appendChild(el('div', 'sr-meta',
      `${courseName(tpl.courseId)} · ${days} at ${tpl.time} · ${k.label.toLowerCase()}`));

    /* The occurrences themselves, which is where a term's worth of small
       irregularities get dealt with one at a time. */
    const ups = occurrences(tpl, Date.now(), Date.now() + SERIES_HORIZON_DAYS * DAY_MS);
    if (!ups.length) {
      row.appendChild(el('div', 'sr-none', tpl.paused ? 'paused' : 'nothing due in the next three weeks'));
    } else {
      const list = el('div', 'sr-occs');
      ups.forEach((o) => list.appendChild(seriesOccRow(tpl, o)));
      row.appendChild(list);
    }

    host.appendChild(row);
  });
}

export function seriesOccRow(tpl, o) {
  const row = el('div', 'sr-occ' + (o.skipped ? ' is-skipped' : '') + (o.moved ? ' is-moved' : ''));
  const inst = instanceOf(tpl.id, o.occ);

  const when = o.skipped ? fmtDay(fromIsoDay(o.occ)) : `${fmtDay(fromIsoDay(o.day))} ${o.time}`;
  row.appendChild(el('span', 'sr-occ-when', when));

  if (o.skipped) row.appendChild(el('span', 'sr-occ-tag', 'skipped'));
  else if (o.moved) row.appendChild(el('span', 'sr-occ-tag', `moved from ${fmtDay(fromIsoDay(o.occ))}`));
  else if (inst && inst.done) row.appendChild(el('span', 'sr-occ-tag', `done · ${fmtDurMin(inst.actualMin || 0)}`));
  else if (inst && inst.attrs) {
    const q = quantityAttr(tpl.kind);
    const bits = choiceAttrs(tpl.kind).map((a) => inst.attrs[a.key]);
    if (q) bits.push(`${inst.attrs[q.key]} ${q.plural}`);
    row.appendChild(el('span', 'sr-occ-tag', bits.join(' · ')));
  }

  const acts = el('span', 'sr-occ-acts');

  const skip = el('button', 'icon-btn', o.skipped ? '↩' : '⊘');
  skip.title = o.skipped ? 'Put this week back' : 'Skip this one — reading week, cancelled class, already done';
  skip.addEventListener('click', () => toggleSkip(tpl.id, o.occ));
  acts.appendChild(skip);

  if (!o.skipped) {
    const move = el('button', 'icon-btn', '⇄');
    move.title = 'Move this one — a different day or a different time, just this week';
    move.addEventListener('click', () => openOccMove(tpl.id, o));
    acts.appendChild(move);
  }

  row.appendChild(acts);
  return row;
}

/** Pull the untouched future instances of a template out of the task list. */
export function withdrawFuture(tplId) {
  S.tasks = S.tasks.filter((t) =>
    !(t.series && t.series.templateId === tplId && !t.done && !wasStarted(t)));
  touchPlan();
}

export function toggleSkip(tplId, occ) {
  const tpl = templateById(tplId);
  if (!tpl) return;
  const ex = tpl.exceptions[occ] || {};

  if (ex.skip) {
    delete ex.skip;
    if (!Object.keys(ex).length) delete tpl.exceptions[occ];
    else tpl.exceptions[occ] = ex;
  } else {
    tpl.exceptions[occ] = { ...ex, skip: true };
  }

  /* A skipped occurrence is an evening given back, and a series runs the whole
     term — so this is a horizon change as much as a weekly one. */
  touchPlan();
  save(); generateSeries(); renderSeriesPanel(); renderList(); renderWeekly();
  if (S.ui.tab === 'longterm') renderLongterm();
  toast(ex.skip ? 'Back on the schedule.' : 'Skipped — just this one.');
}

/** Re-date a single occurrence. The series around it does not move. */
export function openOccMove(tplId, o) {
  const tpl = templateById(tplId);
  if (!tpl) return;

  askText('Move this one', `New date and time for ${fmtDay(fromIsoDay(o.occ))} — as YYYY-MM-DD HH:MM`,
    `${o.day} ${o.time}`, (raw) => {
      const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})$/.exec(raw.trim());
      if (!m || fromIsoDay(m[1]) == null || hhmmToMin(m[2]) == null) {
        toast('Needs to read like 2026-10-08 17:00.', true);
        return;
      }
      tpl.exceptions[o.occ] = { ...(tpl.exceptions[o.occ] || {}), moveTo: m[1], time: m[2] };
      delete tpl.exceptions[o.occ].skip;
      touchPlan();
      save(); generateSeries(); renderSeriesPanel(); renderList(); renderWeekly();
      if (S.ui.tab === 'longterm') renderLongterm();
      toast(`Moved to ${fmtDay(fromIsoDay(m[1]))} at ${m[2]} — this one only.`);
    });
}

export function openSeriesEditor(id) {
  const tpl = id ? templateById(id) : null;
  seriesEditId = tpl ? tpl.id : null;

  $('srModalTitle').textContent = tpl ? 'Edit the series' : 'Something that repeats';
  $('srSave').textContent = tpl ? 'Save changes' : 'Create it';
  $('srDelete').hidden = !tpl;

  const kindSel = $('srKind');
  kindSel.textContent = '';
  SERIES_KINDS.forEach((k) => kindSel.appendChild(new Option(k.label, k.key)));
  kindSel.value = tpl ? tpl.kind : 'reading-post';

  const sel = $('srCourse');
  sel.textContent = '';
  S.courses.forEach((c) => sel.appendChild(new Option(c.name, c.id)));
  sel.value = (tpl && tpl.courseId) || (S.courses[0] || {}).id || '';

  $('srTitle').value = tpl ? tpl.title : '';
  $('srTime').value = tpl ? tpl.time : '09:00';
  $('srFrom').value = tpl && tpl.from ? tpl.from : isoDay(Date.now());
  $('srUntil').value = tpl && tpl.until ? tpl.until : '';
  $('srNotes').value = tpl ? tpl.notes : '';

  const est = tpl ? tpl.estMin : 60;
  $('srEstH').value = Math.floor(est / 60);
  $('srEstM').value = est % 60;

  // seven toggles, because "Tuesdays" is how a recurring thing is actually said
  const days = $('srDays');
  days.textContent = '';
  DOW.forEach((name, jsDay) => {
    const b = el('button', 'wp-dow' + (tpl && tpl.days.includes(jsDay) ? ' is-on' : ''), name);
    b.type = 'button';
    b.dataset.day = String(jsDay);
    b.addEventListener('click', () => b.classList.toggle('is-on'));
    days.appendChild(b);
  });

  updateSeriesKindNote();
  openModal('srModal');
  setTimeout(() => $('srTitle').focus(), 30);
}

export function updateSeriesKindNote() {
  const k = seriesKind($('srKind').value);
  $('srKindNote').textContent = k.explain;
  const q = quantityAttr(k.key);
  $('srEstNote').textContent = q
    ? `The estimate here is only the opening bid: each one asks how many ${q.plural} before the clock starts, and re-estimates from what your ${q.plural} actually cost.`
    : 'Every occurrence starts with this estimate.';
}

export function saveSeriesTemplate() {
  const title = $('srTitle').value.trim();
  if (!title) { toast('Give it a name — it will be the name of every task it makes.', true); return; }

  const days = [...$('srDays').children].filter((b) => b.classList.contains('is-on')).map((b) => +b.dataset.day);
  if (!days.length) { toast('Pick at least one day — that is what makes it recur.', true); return; }

  const from = $('srFrom').value || null;
  const until = $('srUntil').value || null;
  if (from && until && fromIsoDay(until) < fromIsoDay(from)) { toast('It ends before it starts.', true); return; }

  const patch = {
    kind: $('srKind').value,
    title,
    courseId: $('srCourse').value,
    days,
    time: $('srTime').value || '09:00',
    from, until,
    estMin: (+$('srEstH').value || 0) * 60 + (+$('srEstM').value || 0),
    notes: $('srNotes').value.trim(),
  };

  const existing = seriesEditId ? templateById(seriesEditId) : null;
  if (existing) {
    /* Editing the pattern must not strand instances generated under the old
       one: the untouched future is withdrawn and regenerated, while anything
       already started or finished stays exactly where it is. */
    Object.assign(existing, patch);
    withdrawFuture(existing.id);
  } else {
    S.series.templates.push({ id: uid(), created: Date.now(), paused: false, exceptions: {}, deadlineId: null, ...patch });
  }

  /* A template is the single largest thing on the horizon — a Tuesday reading
     is sixteen readings, and the engine walks the whole term's occurrences
     rather than the three weeks that get materialised. */
  touchPlan();
  save();
  const { made } = generateSeries();
  closeModal('srModal');
  renderSeriesPanel(); renderList(); renderWeekly();
  if (S.ui.tab === 'longterm') renderLongterm();
  toast(existing ? `Series updated — ${made} upcoming task${made === 1 ? '' : 's'} rebuilt.` : `Created — ${made} task${made === 1 ? '' : 's'} in the next three weeks.`);
}

export function deleteSeriesTemplate() {
  const tpl = seriesEditId ? templateById(seriesEditId) : null;
  if (!tpl) return;

  withdrawFuture(tpl.id);
  S.series.templates = S.series.templates.filter((t) => t.id !== tpl.id);
  /* A finished instance keeps its back-reference even though the template is
     gone. It is not a live link any more — it is the record of what was
     measured, and `rateStats()` reads the kind and the attributes off it.
     Deleting a series must not delete the evidence it produced. Only the
     unfinished ones, which no longer refer to anything, are cut loose. */
  S.tasks.forEach((t) => { if (t.series && t.series.templateId === tpl.id && !t.done) delete t.series; });

  touchPlan();
  save();
  closeModal('srModal');
  renderSeriesPanel(); renderList(); renderWeekly();
  if (S.ui.tab === 'longterm') renderLongterm();
  toast('Series deleted. What you already finished stays counted.');
}

export function renderSeriesRates() {
  const host = $('seriesRates');
  if (!host) return;
  host.textContent = '';

  const rates = rateStats();
  if (!rates.length) {
    host.appendChild(el('div', 'empty-list',
      'Nothing measured yet. Finish two of the same kind and studyHub will start predicting the next one from its length rather than from an average.'));
    return;
  }

  rates.forEach((r) => {
    const row = el('div', 'sr-rate');
    row.appendChild(el('div', 'sr-rate-head',
      `${r.facet ? `${r.facet} ` : ''}${r.kindLabel.toLowerCase()} · ${r.group}`));
    row.appendChild(el('div', 'sr-rate-num', `${r.medianMinPerUnit} min per ${r.unit}`));
    row.appendChild(el('div', 'sr-rate-meta',
      `${r.n} finished · typically ${r.medianQty} ${r.unit}s, ${fmtDurMin(r.medianActualMin)}`));
    host.appendChild(row);
  });
}

export function initSeries() {
  $('seriesToggle').addEventListener('click', () => {
    const opening = !document.body.classList.contains('series-open');
    setDrawer(opening ? 'series' : null);
    if (opening) { generateSeries(); renderSeriesPanel(); }
  });
  $('seriesClose').addEventListener('click', () => setDrawer(null));
  $('seriesAdd').addEventListener('click', () => openSeriesEditor(null));

  $('srSave').addEventListener('click', saveSeriesTemplate);
  $('srDelete').addEventListener('click', deleteSeriesTemplate);
  $('srKind').addEventListener('change', updateSeriesKindNote);

  $('intakeConfirm').addEventListener('click', confirmIntake);
  ['intakeEstH', 'intakeEstM'].forEach((id) => $(id).addEventListener('input', () => {
    // typing over the offer is allowed, and stops the offer from overwriting it
    $('intakeBasis').dataset.rung = 'yours';
  }));
}

/* ═════════════════════ 8. TEMPLATES + DESIGN ═════════════════════

   Two layers, and the second beats the first for free:

     TEMPLATE   one attribute on <html>; a stylesheet does the rest. JS owns
                only the note palette, which reaches the DOM as an inline
                custom property and so cannot be reached by a stylesheet.

     DESIGN     the user's own answers, written as inline custom properties on
                <html>. An inline declaration outranks every stylesheet rule,
                so a slider overrides a template without either knowing the
                other exists. null means "leave it to the template", which is
                what lets a template switch still show through afterwards. */

/** Push S.design onto <html>. Removing a property is how "use the template's
    answer" is expressed — there is no value that means it. */
